// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { toast } from 'react-toastify';
import { CircularProgress } from '@material-ui/core';

// Internal Dependencies
import { updateModalContent, toggleModal } from '../../../actions/modal';
import MText from '../../common/MText';
import MButton from '../../common/MButton';
import Colours from '../../../styles/colours';
import apiService from '../../../services.js/apiService';
import TagSortable from './tagSortable';
import { IsArray, IsEmpty } from '../../../common/checks';
import useDebounce from '../../global/use-debounce';

const PopularTagsTable = (props) => {
  const table = useRef(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  const debouncedSearch = useDebounce(inputValue, 400);

  const buildPoiPopularTableData = (tags) => {
    let displayOrder = 1;
    return tags.map((tag) => {
      if (IsEmpty(tag.poiRank)) {
        return {
          ...tag,
          displayOrder: '',
        };
      }
      const row = {
        ...tag,
        displayOrder,
      };
      displayOrder += 1;
      return row;
    });
  };

  const fetchPopularTags = async () => {
    try {
      setLoading(true);
      const result = await apiService.getTagsWithLinkedPOIs({ isPoiPopular: true });
      const tags = IsArray(result) ? result : [];
      setTableData(buildPoiPopularTableData(tags));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopularTags();
  }, []);

  useEffect(() => {
    if (IsEmpty(debouncedSearch) || IsEmpty(debouncedSearch.trim())) {
      setSearchResults([]);
      return;
    }

    const fetchTags = async () => {
      try {
        setSearchLoading(true);
        const excludeNames = tableData
          .map((tag) => tag.name)
          .filter((name) => !IsEmpty(name));
        const result = await apiService.getTagsWithLinkedPOIs({
          name: debouncedSearch,
          excludeNames,
        });
        const tags = IsArray(result) ? result : [];
        setSearchResults(tags);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchTags();
  }, [debouncedSearch, tableData]);

  const getNextPoiRank = () => {
    if (IsEmpty(tableData)) {
      return 0;
    }
    const ranks = tableData
      .map(({ poiRank }) => (IsEmpty(poiRank) ? null : Number(poiRank)))
      .filter((rank) => rank !== null);
    const max = ranks.length ? Math.max(...ranks) : -1;
    return max + 1;
  };

  const addTagToTable = async (tag) => {
    try {
      if (tableData.some((t) => t.name === tag.name)) {
        toast.info('Tag already added to popular tags');
        return;
      }
      if (IsEmpty(tag.isApproved)) {
        toast.info('Cannot add unapproved tag');
        return;
      }
      const nextRank = getNextPoiRank();
      await apiService.updateTag({ name: tag.name, isPoiPopular: true, poiRank: nextRank });
      const updatedTag = {
        ...tag,
        isPoiPopular: true,
        poiRank:      nextRank,
      };
      setTableData((current) => buildPoiPopularTableData([...current, updatedTag]));
      toast.success(`Tag "${tag.name}" marked as popular`);
      setInputValue('');
      setSearchResults([]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchPoisData = async (tag) => {
    try {
      const pois = await apiService.getPoisByTag({ tag: tag.name });
      return pois;
    } catch (err) {
      toast.error(err.message);
      return { result: [], totalCount: 0 };
    }
  };

  const handleDelete = async (tag) => {
    props.updateModalContent({
      title: (
        <MText text="Remove Confirmation" size={16} bold />
      ),
      content: (
        <MText
          text={(
            <>
              Remove
              {' '}
              <strong>{tag.name}</strong>
              {' '}
              from popular tags?
            </>
          )}
          size={14}
        />
      ),
      cancelText:   'Cancel',
      actionButton: {
        actionText: 'Remove',
        actionType: 'danger',
        action:     async () => {
          try {
            await apiService.updateTag({ name: tag.name, isPoiPopular: false, poiRank: null });
            toast.success(`Tag "${tag.name}" removed from popular tags`);
            setTableData((current) => buildPoiPopularTableData(
              current.filter((t) => t.name !== tag.name),
            ));
          } catch (err) {
            toast.error(`Failed to remove tag: ${err.message}`);
          }
        },
      },
      centered:         true,
      closeAfterAction: true,
    });
    props.toggleModal();
  };

  const handleViewManageOrder = () => {
    const ordered = [...tableData]
      .map((t) => ({ ...t, id: t.name, title: t.name }));
    let currentTags = ordered;
    props.updateModalContent({
      title:   'Popular Tags',
      width:   'sm',
      content: <TagSortable
        tags={currentTags}
        handleSort={((newTags) => {
          currentTags = newTags;
        })}
      />,
      cancelText:   'Cancel',
      actionButton: {
        actionText: 'Save',
        actionType: 'primary',
        action:     async () => {
          try {
            const payloads = currentTags.map((t, idx) => ({
              name:         t.name,
              poiRank:      idx,
              isPoiPopular: true,
            }));
            await apiService.bulkUpdateTags({ tags: payloads });
            toast.success('Tag order saved');
            await fetchPopularTags();
          } catch (err) {
            toast.error(`Failed to save tag order: ${err.message}`);
          }
        },
      },
      closeAfterAction: true,
      centered:         true,
    });
    props.toggleModal();
  };

  const handleViewLinkedPois = async (tag) => {
    const poisData = await fetchPoisData(tag);

    props.updateModalContent({
      title: (
        <div className="d-flex align-items-center mt-2 mx-2">
          <MText text="Linked POIs for" size={16} bold />
          <div
            style={{
              backgroundColor: tag.isApproved ? Colours.Green1 : Colours.Red,
              borderRadius:    3,
              padding:         '2px 8px',
              marginLeft:      8,
            }}
          >
            <MText text={tag.name} bold style={{ color: Colours.White }} />
          </div>
        </div>
      ),
      content: (
        <div className="mx-2">
          <MaterialTable
            columns={[
              {
                title:     'No.',
                field:     'index',
                render:    (rowData) => <MText text={rowData.tableData.id + 1} />,
                cellStyle: {
                  whiteSpace: 'nowrap',
                  width:      '10%',
                },
              },
              {
                title:  'Name',
                field:  'name',
                render: (rowData) => (
                  <a href={`/admin/poi/${rowData.id}`} target="_blank" rel="noopener noreferrer">
                    {rowData.name}
                  </a>
                ),
              },
            ]}
            data={poisData.result}
            options={{
              pageSize:            10,
              search:              false,
              sorting:             false,
              draggable:           false,
              emptyRowsWhenPaging: false,
              toolbar:             false,
              padding:             'dense',
            }}
          />
        </div>
      ),
      centered:         true,
      cancelText:       'Close',
      closeAfterAction: true,
    });
    props.toggleModal();
  };

  const columns = [
    {
      title:  'Display Order',
      field:  'displayOrder',
      render: (r) => (
        <MText text={r.displayOrder} />
      ),
      cellStyle: {
        textAlign: 'center',
        width:     '15%',
      },
      headerStyle: {
        textAlign: 'center',
        width:     '15%',
      },
    },
    {
      title:  'Tag',
      field:  'name',
      render: (r) => (
        <div
          style={{
            display:         'inline-block',
            borderRadius:    3,
            padding:         '6px 10px',
            backgroundColor: r.isApproved ? Colours.Green1 : Colours.Red,
          }}
        >
          <MText text={r.name} style={{ color: Colours.White }} bold />
        </div>
      ),
    },
    {
      title:  'Total POIs',
      field:  'linkedPois',
      render: (r) => (
        <MText text={r.linkedPois || 0} />
      ),
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
  ];

  return (
    <div>
      <div
        className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3"
        style={{ gap: '10px' }}
      >
        <div className="flex-grow-1 w-100 w-md-50">
          <FormControl style={{ width: '100%' }}>
            <Autocomplete
              size="small"
              options={searchResults}
              getOptionLabel={(option) => option.name || ''}
              getOptionDisabled={(option) => !option.isApproved}
              noOptionsText={IsEmpty(debouncedSearch) ? '' : 'No result found'}
              inputValue={inputValue}
              popupIcon={
                searchLoading ? <CircularProgress color="inherit" size={16} /> : null
              }
              loading={searchLoading}
              open={open && Boolean(debouncedSearch.trim())}
              onOpen={() => {
                if (debouncedSearch.trim()) {
                  setOpen(true);
                }
              }}
              onClose={() => {
                setOpen(false);
                setInputValue('');
              }}
              onInputChange={(_, value, reason) => {
                setInputValue(value);
                if (reason === 'input') {
                  setOpen(Boolean(value.trim()));
                }
              }}
              onChange={(_, value) => {
                if (!IsEmpty(value) && !value.isApproved) {
                  toast.info('Cannot add unapproved tag');
                  return;
                }
                if (!IsEmpty(value)) {
                  addTagToTable(value);
                }
                setOpen(false);
                setInputValue('');
              }}
              renderOption={(option) => (
                <div className="w-100 py-2 px-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div
                      style={{
                        display:         'inline-block',
                        borderRadius:    3,
                        padding:         '4px 10px',
                        backgroundColor: option.isApproved ? Colours.Green1 : Colours.Red,
                      }}
                    >
                      <MText text={option.name} bold style={{ color: Colours.White }} />
                    </div>

                    {option.isApproved && (
                      <MButton
                        text="+"
                        variant="contained"
                        colour={Colours.White}
                        textColour={Colours.Black}
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          border:         `1px solid ${Colours.Gray2}`,
                          borderRadius:   '50%',
                          marginBottom:   -20,
                          minWidth:       26,
                          minHeight:      26,
                          padding:        0,
                          fontSize:       16,
                          lineHeight:     '16px',
                          display:        'flex',
                          alignItems:     'center',
                          justifyContent: 'center',
                        }}
                        disabled
                      />
                    )}
                  </div>

                  <div className="mt-1">
                    <MText
                      text={`Linked to ${option.linkedPois || 0} POIs`}
                      style={{ fontSize: 12 }}
                    />
                  </div>
                </div>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search tags..."
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: 13, padding: '3px 5px' },
                  }}
                />
              )}
            />
          </FormControl>
        </div>

        <div className="d-flex justify-content-end w-100 w-md-auto">
          <MButton
            text="Manage Order"
            variant="contained"
            colour={Colours.MaterialBlue}
            textColour={Colours.White}
            style={{ padding: '8px 14px', fontSize: 14, minHeight: 28, textTransform: 'uppercase' }}
            onClick={handleViewManageOrder}
          />
        </div>
      </div>

      <MaterialTable
        tableRef={table}
        className="material-table"
        title=""
        columns={columns}
        data={tableData}
        isLoading={loading}
        actions={[
          {
            icon:    'visibility',
            tooltip: 'View Linked POIs',
            onClick: (_, tag) => handleViewLinkedPois(tag),
          },
          {
            icon:    'delete',
            tooltip: 'Delete Tag',
            onClick: (_, tag) => handleDelete(tag),
          },
        ]}
        options={{
          paging:              false,
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
          draggable:           false,
        }}
      />
    </div>
  );
};

PopularTagsTable.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { updateModalContent, toggleModal })(PopularTagsTable);
